import { AlertCircle, ArrowLeft, Calendar, CheckSquare, Copy, FileText, Hash, Save, Square } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useAuth } from "@/features/sistema/hooks/useAuth";
import { useDarkMode } from "@/features/sistema/hooks/useDarkMode";
import { useRouteRefresh } from "@/features/sistema/hooks/useRouteRefresh";
import { supabase } from "@/features/sistema/services/supabaseClient";

interface MaoItem {
  nome: string;
  quantidade: number;
}

interface EquipItem {
  nome: string;
  quantidade: number;
}

interface AtividadeItem {
  descricao: string;
  porcentagem: number;
  status: "Em andamento" | "Concluída" | "Pausada";
}

interface UltimoDiario {
  mao_obra: MaoItem[] | null;
  equipamentos: EquipItem[] | null;
  atividades: string | null; // JSON string
}

interface Obra {
  id: string;
  nome: string;
}

interface DiarioNovoProps {
  // Removido modoEscuro prop
}

export default function DiarioNovo({}: DiarioNovoProps) {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [isDarkMode] = useDarkMode();
  
  const obraId = params?.id ?? null;
  const [obra, setObra] = useState<Obra | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [proximoNumero, setProximoNumero] = useState<number>(1);
  const [dataHoje] = useState<string>(new Date().toISOString().split("T")[0]);
  const [ultimoDiario, setUltimoDiario] = useState<UltimoDiario | null>(null);
  const [copiarDados, setCopiarDados] = useState<boolean>(true); // ✅ Novo estado para controlar cópia

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/');
      return;
    }
  }, [obraId, user, authLoading, navigate]);

  // ✅ Carregar SOMENTE ao entrar/mudar de rota (ou quando dispararmos app:refresh)
  useRouteRefresh(() => inicializarNovoDiario());

  async function inicializarNovoDiario() {
    try {
      setLoading(true);
      setErro(null);

      if (!obraId) {
        setErro("ID da obra não fornecido.");
        return;
      }

      // Carregar obra
      const { data: obraData, error: obraError } = await supabase
        .from("obras")
        .select("id, nome")
        .eq("id", obraId)
        .single();

      if (obraError) throw obraError;
      setObra(obraData);

      // Buscar último relatório da obra para pegar o número + dados para copiar
      const { data: ultimoRelatorio, error: ultimoError } = await supabase
        .from("relatorios")
        .select("numero, mao_obra, equipamentos, atividades")
        .eq("obra_id", obraId)
        .order("numero", { ascending: false })
        .limit(1)
        .single();

      if (ultimoError && ultimoError.code !== "PGRST116") {
        // PGRST116 = no rows returned (primeira vez)
        throw ultimoError;
      }

      if (ultimoRelatorio) {
        setProximoNumero(ultimoRelatorio.numero + 1);
        setUltimoDiario({
          mao_obra: ultimoRelatorio.mao_obra,
          equipamentos: ultimoRelatorio.equipamentos,
          atividades: ultimoRelatorio.atividades
        });
      } else {
        setProximoNumero(1);
        setUltimoDiario(null);
      }

    } catch (err) {
      setErro("Erro ao inicializar: " + (err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function criarDiario() {
    if (!obraId || !user) return;

    try {
      setSaving(true);
      setErro(null);

      const newId = crypto.randomUUID();
      const agora = new Date().toISOString();

      // Dados a serem inseridos - só copia se checkbox estiver marcado
      const dadosInsert = {
        id: newId,
        numero: proximoNumero,
        data: dataHoje,
        status: "rascunho" as const,
        obra_id: obraId,
        criado_por: user.id,
        ultima_modificacao: agora,
        // ✅ Só copiar dados se checkbox estiver marcado E se existe último diário
        mao_obra: (copiarDados && ultimoDiario?.mao_obra) ? ultimoDiario.mao_obra : null,
        equipamentos: (copiarDados && ultimoDiario?.equipamentos) ? ultimoDiario.equipamentos : null,
        atividades: (copiarDados && ultimoDiario?.atividades) ? ultimoDiario.atividades : null,
        // Campos vazios iniciais
        clima_manha: null,
        clima_tarde: null,
        clima_noite: null,
        ocorrencias: null,
        comentarios: null,
      };

      const { error } = await supabase
        .from("relatorios")
        .insert(dadosInsert);

      if (error) throw error;

      // 🔹 avisar telas relacionadas para recarregar
      window.dispatchEvent(new CustomEvent('app:refresh', { detail: { scope: `/obras/${obraId}/diarios` } }));
      window.dispatchEvent(new CustomEvent('app:refresh', { detail: { scope: `/obras/${obraId}/diarios/${newId}` } }));
      // Redirecionar para edição
      navigate(`/obras/${obraId}/diarios/${newId}/editar`);
      
    } catch (err) {
      setErro("Erro ao criar diário: " + (err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  const parseAtividades = (): AtividadeItem[] => {
    if (!ultimoDiario?.atividades) return [];
    try {
      return JSON.parse(ultimoDiario.atividades);
    } catch {
      return [];
    }
  };

  if (!obraId) {
    return (
      <div className="min-h-screen bg-background dark:bg-gray-900 flex items-center justify-center transition-colors">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-confirmation dark:border-green-400 mx-auto mb-4"></div>
          <p className="text-foreground dark:text-gray-100 font-ageo-regular">Carregando contexto...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background dark:bg-gray-900 flex items-center justify-center transition-colors">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-confirmation dark:border-green-400 mx-auto mb-4"></div>
          <p className="text-foreground dark:text-gray-100 font-ageo-regular">Preparando novo diário...</p>
        </div>
      </div>
    );
  }

  if (erro && !obra) {
    return (
      <div className="min-h-screen bg-background dark:bg-gray-900 flex items-center justify-center transition-colors">
        <div className="text-center p-6 bg-critical/10 dark:bg-red-900/20 border border-critical/20 dark:border-red-800/20 rounded-lg transition-colors">
          <AlertCircle className="w-12 h-12 text-critical dark:text-red-400 mx-auto mb-4" />
          <p className="text-critical dark:text-red-400 font-ageo-regular">{erro}</p>
          <Link to={`/obras/${obraId}`} className="mt-4 inline-block">
            <button className="px-4 py-2 bg-primary dark:bg-blue-600 text-white rounded-md hover:bg-primary/90 dark:hover:bg-blue-700 font-ageo-regular transition-colors">
              Voltar para Obra
            </button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-[100dvh] font-ageo-regular transition-colors ${
      isDarkMode
        ? "bg-gray-900 text-gray-100"
        : "bg-background text-foreground"
    }`}>
      <div className="w-full max-w-[1600px] 2xl:max-w-[1920px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4">
        {/* Header */}
        <div className="flex flex-row items-center justify-between gap-2 mb-4">
          <Link to={`/obras/${obraId}/diarios`}>
            <button className="px-3 py-1 bg-secondary dark:bg-gray-700 text-foreground dark:text-gray-100 hover:bg-secondary/80 dark:hover:bg-gray-600 rounded-md text-sm font-ageo-regular focus:ring-2 focus:ring-primary dark:focus:ring-blue-400 transition-colors">
              <ArrowLeft className="w-4 h-4 inline mr-1" />
              Voltar
            </button>
          </Link>
          <h1 className="text-base sm:text-lg font-ageo-bold text-foreground dark:text-gray-100 truncate flex-1 text-center">
            Novo Diário - {obra?.nome}
          </h1>
          <button
            onClick={criarDiario}
            disabled={saving}
            className="px-3 py-1 bg-confirmation dark:bg-green-600 text-white hover:bg-confirmation/90 dark:hover:bg-green-700 rounded-md text-sm font-ageo-regular focus:ring-2 focus:ring-confirmation dark:focus:ring-green-400 disabled:opacity-60 transition-colors"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline mr-1"></div>
                Criando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 inline mr-1" />
                Criar Diário
              </>
            )}
          </button>
        </div>

        <div className="space-y-5 max-w-4xl mx-auto">
          {/* Erro */}
          {erro && (
            <div className="bg-critical/10 dark:bg-red-900/20 border border-critical/20 dark:border-red-800/20 rounded-lg p-4 transition-colors">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-critical dark:text-red-400 flex-shrink-0" />
                <p className="text-critical dark:text-red-400 font-ageo-regular">{erro}</p>
              </div>
            </div>
          )}

          {/* Informações do Novo Diário */}
          <section className="bg-background dark:bg-gray-800 rounded-lg shadow-md p-5 border border-secondary dark:border-gray-700 transition-colors">
            <h2 className="text-lg font-ageo-bold text-foreground dark:text-gray-100 mb-6 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Informações do Novo Diário
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm text-foreground/70 dark:text-gray-400 mb-2 flex items-center gap-2">
                  <Hash className="w-4 h-4" />
                  Número do Diário
                </label>
                <div className="bg-secondary/30 dark:bg-gray-700/50 px-4 py-3 rounded-md">
                  <span className="text-lg font-ageo-bold text-foreground dark:text-gray-100">#{proximoNumero}</span>
                </div>
                <p className="text-xs text-foreground/60 dark:text-gray-400 mt-1">
                  Número automático sequencial
                </p>
              </div>

              <div>
                <label className="block text-sm text-foreground/70 dark:text-gray-400 mb-2 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Data do Diário
                </label>
                <div className="bg-secondary/30 dark:bg-gray-700/50 px-4 py-3 rounded-md">
                  <span className="text-lg font-ageo-bold text-foreground dark:text-gray-100">
                    {new Date(dataHoje).toLocaleDateString('pt-BR')}
                  </span>
                </div>
                <p className="text-xs text-foreground/60 dark:text-gray-400 mt-1">
                  Data de hoje (automático)
                </p>
              </div>

              <div>
                <label className="block text-sm text-foreground/70 dark:text-gray-400 mb-2">
                  Status Inicial
                </label>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 px-4 py-3 rounded-md">
                  <span className="text-yellow-800 dark:text-yellow-400 font-ageo-regular">Rascunho</span>
                </div>
                <p className="text-xs text-foreground/60 dark:text-gray-400 mt-1">
                  Pode ser alterado depois
                </p>
              </div>
            </div>
          </section>

          {/* Dados Copiados do Último Diário */}
          {ultimoDiario ? (
            <section className="bg-background dark:bg-gray-800 rounded-lg shadow-md p-5 border border-secondary dark:border-gray-700 transition-colors">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-ageo-bold text-foreground dark:text-gray-100 flex items-center gap-2">
                  <Copy className="w-5 h-5" />
                  Dados do Último Diário
                </h2>
                {/* ✅ Checkbox para controlar se deve copiar os dados */}
                <button
                  type="button"
                  onClick={() => setCopiarDados(!copiarDados)}
                  className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-secondary/20 dark:hover:bg-gray-700/20 transition-colors"
                >
                  {copiarDados ? (
                    <CheckSquare className="w-5 h-5 text-confirmation dark:text-green-400" />
                  ) : (
                    <Square className="w-5 h-5 text-foreground/50 dark:text-gray-400" />
                  )}
                  <span className="text-sm font-ageo-regular text-foreground dark:text-gray-100">
                    Copiar dados do último diário
                  </span>
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Mão de Obra */}
                <div className={`transition-opacity ${copiarDados ? 'opacity-100' : 'opacity-50'}`}>
                  <h3 className="font-ageo-bold text-foreground dark:text-gray-100 mb-3">👷 Mão de Obra</h3>
                  <div className="bg-green-50/80 dark:bg-green-900/20 border border-green-200/50 dark:border-green-800/50 rounded-lg p-4 max-h-40 overflow-y-auto transition-colors">
                    {ultimoDiario.mao_obra && ultimoDiario.mao_obra.length > 0 ? (
                      <div className="space-y-2">
                        {ultimoDiario.mao_obra.map((item, index) => (
                          <div key={index} className="flex justify-between text-sm">
                            <span className="text-foreground dark:text-gray-100">{item.nome}</span>
                            <span className="text-foreground dark:text-gray-100 font-ageo-bold">{item.quantidade}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-foreground/70 dark:text-gray-400 font-ageo-regular">Nenhuma mão de obra</p>
                    )}
                  </div>
                </div>

                {/* Equipamentos */}
                <div className={`transition-opacity ${copiarDados ? 'opacity-100' : 'opacity-50'}`}>
                  <h3 className="font-ageo-bold text-foreground dark:text-gray-100 mb-3">🔧 Equipamentos</h3>
                  <div className="bg-green-50/80 dark:bg-green-900/20 border border-green-200/50 dark:border-green-800/50 rounded-lg p-4 max-h-40 overflow-y-auto transition-colors">
                    {ultimoDiario.equipamentos && ultimoDiario.equipamentos.length > 0 ? (
                      <div className="space-y-2">
                        {ultimoDiario.equipamentos.map((item, index) => (
                          <div key={index} className="flex justify-between text-sm">
                            <span className="text-foreground dark:text-gray-100">{item.nome}</span>
                            <span className="text-foreground dark:text-gray-100 font-ageo-bold">{item.quantidade}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-foreground/70 dark:text-gray-400 font-ageo-regular">Nenhum equipamento</p>
                    )}
                  </div>
                </div>

                {/* Atividades */}
                <div className={`transition-opacity ${copiarDados ? 'opacity-100' : 'opacity-50'}`}>
                  <h3 className="font-ageo-bold text-foreground dark:text-gray-100 mb-3">⚡ Atividades</h3>
                  <div className="bg-green-50/80 dark:bg-green-900/20 border border-green-200/50 dark:border-green-800/50 rounded-lg p-4 max-h-40 overflow-y-auto transition-colors">
                    {parseAtividades().length > 0 ? (
                      <div className="space-y-2">
                        {parseAtividades().map((item, index) => (
                          <div key={index} className="text-sm">
                            <div className="font-ageo-regular text-foreground dark:text-gray-100 line-clamp-2">{item.descricao}</div>
                            <div className="flex justify-between text-xs text-foreground/70 dark:text-gray-400 mt-1">
                              <span>{item.porcentagem}%</span>
                              <span>{item.status}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-foreground/70 dark:text-gray-400 font-ageo-regular">Nenhuma atividade</p>
                    )}
                  </div>
                </div>
              </div>

              <div className={`mt-4 p-3 border rounded-lg transition-all ${
                copiarDados 
                  ? 'bg-confirmation/10 dark:bg-green-900/20 border-confirmation/20 dark:border-green-400/20' 
                  : 'bg-gray-50/80 dark:bg-gray-700/20 border-gray-200/50 dark:border-gray-600/20'
              }`}>
                <p className="text-sm text-foreground dark:text-gray-100 font-ageo-regular">
                  {copiarDados 
                    ? '✅ Estes dados serão automaticamente copiados para o novo diário e poderão ser editados após a criação.'
                    : '⚪ O novo diário será criado vazio, sem copiar dados do último diário.'
                  }
                </p>
              </div>
            </section>
          ) : (
            <section className="bg-background dark:bg-gray-800 rounded-lg shadow-md p-5 border border-secondary dark:border-gray-700 transition-colors">
              <h2 className="text-lg font-ageo-bold text-foreground dark:text-gray-100 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Primeiro Diário da Obra
              </h2>
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-primary/10 dark:bg-blue-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-primary dark:text-blue-400" />
                </div>
                <h3 className="text-lg font-ageo-bold text-foreground dark:text-gray-100 mb-2">Primeiro Diário</h3>
                <p className="text-foreground/70 dark:text-gray-400 font-ageo-regular">
                  Este será o primeiro diário desta obra. Será criado vazio e você poderá preencher todas as informações.
                </p>
              </div>
            </section>
          )}

          {/* Próximos Passos */}
          <section className="bg-confirmation/10 dark:bg-green-900/20 border border-confirmation/20 dark:border-green-400/20 rounded-lg p-6 transition-colors">
            <h3 className="text-lg font-ageo-bold text-foreground dark:text-gray-100 mb-4">📋 Próximos Passos</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-confirmation/20 dark:bg-green-400/20 text-confirmation dark:text-green-400 flex items-center justify-center text-xs font-ageo-bold flex-shrink-0 mt-0.5">
                  1
                </div>
                <div>
                  <p className="font-ageo-bold text-foreground dark:text-gray-100">Preencher clima</p>
                  <p className="text-foreground/70 dark:text-gray-400 font-ageo-regular">Condições climáticas do dia (manhã, tarde, noite)</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-confirmation/20 dark:bg-green-400/20 text-confirmation dark:text-green-400 flex items-center justify-center text-xs font-ageo-bold flex-shrink-0 mt-0.5">
                  2
                </div>
                <div>
                  <p className="font-ageo-bold text-foreground dark:text-gray-100">
                    {copiarDados && ultimoDiario ? 'Ajustar dados copiados' : 'Adicionar dados'}
                  </p>
                  <p className="text-foreground/70 dark:text-gray-400 font-ageo-regular">
                    {copiarDados && ultimoDiario ? 'Mão de obra, equipamentos e atividades' : 'Adicionar mão de obra, equipamentos e atividades'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-confirmation/20 dark:bg-green-400/20 text-confirmation dark:text-green-400 flex items-center justify-center text-xs font-ageo-bold flex-shrink-0 mt-0.5">
                  3
                </div>
                <div>
                  <p className="font-ageo-bold text-foreground dark:text-gray-100">Registrar ocorrências</p>
                  <p className="text-foreground/70 dark:text-gray-400 font-ageo-regular">Eventos importantes do dia</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-confirmation/20 dark:bg-green-400/20 text-confirmation dark:text-green-400 flex items-center justify-center text-xs font-ageo-bold flex-shrink-0 mt-0.5">
                  4
                </div>
                <div>
                  <p className="font-ageo-bold text-foreground dark:text-gray-100">Anexar fotos/documentos</p>
                  <p className="text-foreground/70 dark:text-gray-400 font-ageo-regular">Registros visuais e documentos</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}



